import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, MessageSquare, Send, Flag } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { UsernameWithBadge } from "@/components/UsernameWithBadge";
import { hapticMedium, hapticSuccess, hapticError } from "@/utils/haptic";
import { digitalOcean } from "@/integrations/digitalocean/client";

export default function CommentSection({ trackId = null, maxHeight = "600px" }) {
  const queryClient = useQueryClient();
  const [currentUser, setCurrentUser] = useState(null);
  const [commentText, setCommentText] = useState("");

  React.useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const { data: sessionResult } = await digitalOcean.auth.getSession();
      const session = sessionResult?.session;
      
      if (!session || !session.user) {
        setCurrentUser(null);
        return;
      }

      const { data: profile, error } = await digitalOcean
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (error) {
        console.error("Error loading profile:", error);
        return;
      }

      setCurrentUser(profile);
    } catch (error) {
      console.error("Error loading user:", error);
    }
  };

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ['comments', trackId],
    queryFn: async () => {
      if (!trackId) return [];
      
      const { data, error } = await digitalOcean
        .from('comments')
        .select('*')
        .eq('track_id', trackId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error loading comments:", error);
        throw error;
      }

      if (!data || data.length === 0) return [];

      const userIds = Array.from(new Set(data.map(comment => comment.user_id).filter(Boolean)));
      let profilesMap = new Map();

      if (userIds.length > 0) {
        const { data: profilesData, error: profilesError } = await digitalOcean
          .from('public_profiles')
          .select('id, username, avatar_url')
          .in('id', userIds);

        if (!profilesError && profilesData) {
          profilesMap = new Map(profilesData.map(profile => [profile.id, profile]));
        }
      }
      
      return data.map(comment => {
        const profile = profilesMap.get(comment.user_id);
        return {
          ...comment,
          user_name: profile?.username || 'Anonymous',
          user_image: profile?.avatar_url || null,
        };
      });
    },
    enabled: !!currentUser && !!trackId,
  });

  const postCommentMutation = useMutation({
    mutationFn: async () => {
      const { error } = await digitalOcean
        .from('comments')
        .insert({
          track_id: trackId,
          user_id: currentUser.id,
          content: commentText
        });

      if (error) throw error;

      // Get track details for notification
      const { data: track } = await digitalOcean
        .from('tracks')
        .select('artist_id, title')
        .eq('id', trackId)
        .single();

      if (track && track.artist_id !== currentUser.id) {
        await digitalOcean
          .from('notifications')
          .insert({
            user_id: track.artist_id,
            title: 'New Comment',
            message: `${currentUser.username} commented on "${track.title}"`,
            is_read: false
          });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['comments', trackId]);
      setCommentText("");
      hapticSuccess();
      toast.success("Comment posted!");
    },
    onError: (error) => {
      console.error("Error posting comment:", error);
      hapticError();
      toast.error("Failed to post comment");
    }
  });

  const handlePostComment = () => {
    if (commentText.trim() && commentText.length <= 180) {
      hapticMedium();
      postCommentMutation.mutate();
    }
    // Don't trigger haptic error for normal validation - only for actual errors
  };

  if (!currentUser) return null;

  return (
    <div className="neuro-base rounded-3xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="w-5 h-5 text-[#808080]" />
        <h3 className="text-sm font-medium text-[#d0d0d0]">Comments</h3>
        <span className="text-xs text-[#606060]">({comments.length})</span>
      </div>

      {/* Comment Input */}
      <div className="mb-4">
        <Textarea
          value={commentText}
          onChange={(e) => setCommentText(e.target.value.slice(0, 180))}
          placeholder="Leave a comment..."
          className="bg-[#0a0a0a] border-none text-[#d0d0d0] placeholder:text-[#505050] neuro-pressed rounded-2xl min-h-[80px] resize-none mb-2"
        />
        <div className="flex justify-between items-center">
          <span className={`text-xs ${commentText.length >= 180 ? 'text-[#a08080]' : 'text-[#606060]'}`}>
            {commentText.length}/180
          </span>
          <Button
            onClick={handlePostComment}
            disabled={!commentText.trim() || commentText.length > 180 || postCommentMutation.isPending}
            className="neuro-base active:neuro-pressed rounded-2xl px-4 py-2 text-xs"
          >
            {postCommentMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin text-[#808080]" />
            ) : (
              <>
                <Send className="w-4 h-4 text-[#a0a0a0] mr-2" />
                <span className="text-[#a0a0a0]">Post</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Comments List */}
      <div className="space-y-3" style={{ maxHeight, overflowY: 'auto' }}>
        {isLoading ? (
          <div className="text-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-[#707070] mx-auto" />
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-xs text-[#707070]">No comments yet</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="neuro-flat rounded-2xl p-3">
              <div className="flex gap-3">
                {comment.user_image ? (
                  <img
                    src={comment.user_image}
                    alt={comment.user_name}
                    width="32"
                    height="32"
                    className="w-8 h-8 rounded-full object-cover neuro-pressed"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full neuro-pressed flex items-center justify-center">
                    <span className="text-xs text-[#707070]">
                      {comment.user_name?.[0]?.toUpperCase() || '?'}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start mb-1">
                    <p className="text-xs font-medium text-[#d0d0d0]">
                      <UsernameWithBadge userId={comment.user_id} username={comment.user_name} />
                    </p>
                    <span className="text-xs text-[#606060]">
                      {format(new Date(comment.created_at), 'MMM d, h:mm a')}
                    </span>
                  </div>
                  <p className="text-xs text-[#a0a0a0] leading-relaxed break-words">
                    {comment.content}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
